"use client";

/**
 * Per-page recent activity feed shown on /analyze, /calculate, /compare.
 *
 * Each page renders its own `kind` of feed: /analyze shows recent
 * analyses, /calculate shows recent calculations, /compare shows
 * recent comparisons. Feeds never bleed across pages.
 *
 * Signed-in users: pulls /api/history with the matching `source`
 * filter so the server returns only rows for this page's kind.
 * Anonymous users: filters the localStorage feed (which is single-
 * keyed across all kinds) down to this page's kind.
 *
 * Presentation: ONE bordered container of compact single-line rows
 * (verdict dot · place + attrs · price · % · age), table-like and
 * scannable - not a stack of chunky cards.
 *
 * Renders nothing on empty - calling pages keep their layout instead
 * of having an empty card hanging there.
 */
import { signedPct } from "@/lib/format/change";
import { useEffect, useState } from "react";
import Link from "@/lib/localized-link";
import { useAuth } from "@/lib/auth-context";
import { useT, useLocale } from "@/lib/locale-context";
import { getHistory, formatPrice } from "@/lib/api";
import { roundEstimate } from "@/lib/estimate";
import type { HistoryFilters } from "@/lib/api";
import { translateVerdict, type TranslationKey } from "@/lib/i18n";
import { formatMonthDay } from "@/lib/blog-format";
import type { Locale } from "@/lib/i18n-config";
import type { HistoryItem } from "@/types";
import {
  getRecentActivity,
  type RecentActivityItem,
  type RecentKind,
} from "@/lib/recent-activity";
import { ArrowRight, ChevronRight, Trophy } from "lucide-react";

const FEED_SIZE = 5;

interface UnifiedItem {
  key: string;
  href: string | null;
  ts: number;
  mode?: "sale" | "rent" | null;
  verdict?: string | null;
  price?: number | null;
  predicted_price?: number | null;
  overpriced_pct?: number | null;
  location_name?: string | null;
  room_count?: number | null;
  area_m2?: number | null;
  listing_count?: number | null;
  compare_preview?: string | null;
  compare_prices?: number[];
  compare_winner?: number | null;
  url?: string | null;
}

/** Server-side `source` filter for getHistory(). One value per page;
 *  `extract` rows (legacy text-extraction source) aren't surfaced on
 *  any of the three entry-point pages. */
function sourceFilterFor(kind: RecentKind): HistoryFilters["source"] {
  if (kind === "analyze") return "analyze";
  if (kind === "compare") return "compare";
  return "sell";
}

function fromHistoryItem(item: HistoryItem, kind: RecentKind): UnifiedItem {
  // Server returns rows already matching the requested source; the
  // /h?id=N permalink works for analyze + sell, /compare/h/N for
  // compare runs.
  const href = kind === "compare" ? `/compare/h/${item.id}` : `/h?id=${item.id}&from=recent`;
  return {
    key: `srv:${item.kind || "x"}:${item.id}`,
    href,
    ts: new Date(item.created_at).getTime(),
    mode: item.mode ?? null,
    verdict: item.verdict ?? null,
    price: item.price ?? null,
    predicted_price: item.predicted_price ?? null,
    overpriced_pct: item.overpriced_pct ?? null,
    location_name: item.location_name ?? null,
    room_count: item.room_count ?? null,
    area_m2: item.area_m2 ?? null,
    listing_count: item.listing_count ?? null,
    compare_preview: item.compare_preview ?? null,
    compare_prices: item.compare_prices ?? [],
    compare_winner: item.compare_winner ?? null,
    url: item.listing_url ?? null,
  };
}

function fromAnonItem(item: RecentActivityItem): UnifiedItem {
  // Anonymous analyze rows are reopen-able: the /analyze auto-trigger
  // hits the localStorage cache for an exact URL match and skips the
  // API round-trip. Calculate + compare anon rows have no permalink
  // (server didn't persist them) so the row is display-only.
  let href: string | null = null;
  if (item.kind === "analyze" && item.url) {
    href = `/analyze?url=${encodeURIComponent(item.url)}`;
  }
  return {
    key: `loc:${item.id}`,
    href,
    ts: item.ts,
    mode: item.mode ?? null,
    verdict: item.verdict ?? null,
    price: item.price ?? null,
    predicted_price: item.predicted_price ?? null,
    overpriced_pct: item.overpriced_pct ?? null,
    location_name: item.location_name ?? null,
    room_count: item.room_count ?? null,
    area_m2: item.area_m2 ?? null,
    listing_count: item.listing_count ?? null,
    compare_preview: item.compare_preview ?? null,
    url: item.url ?? null,
  };
}

function formatRelative(ts: number, locale: Locale, t: (k: TranslationKey) => string): string {
  const diff = Math.max(0, Date.now() - ts);
  const min = Math.floor(diff / 60_000);
  if (min < 1) return t("recent.justnow");
  if (min < 60) return `${min}${t("recent.unit.min")}`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}${t("recent.unit.hour")}`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}${t("recent.unit.day")}`;
  // 7+ days: full month name via the shared MONTHS_LONG table -
  // dodges the Node-ICU "M" collapse on Azerbaijani month longforms.
  return formatMonthDay(ts, locale);
}

/** Verdict dot color from the deal sign; gray when unknown/fair. */
function dotColor(pct: number | null | undefined): string {
  if (pct == null) return "bg-gray-300";
  if (pct < 0) return "bg-emerald-500";
  if (pct > 5) return "bg-rose-500";
  return "bg-gray-300";
}

function Row({ item, kind }: { item: UnifiedItem; kind: RecentKind }) {
  const t = useT();
  const { locale } = useLocale();

  const time = (
    <span className="shrink-0 text-xs text-gray-500 tabular-nums whitespace-nowrap">
      {formatRelative(item.ts, locale, t)}
    </span>
  );

  let inner: React.ReactNode;
  if (kind === "compare") {
    // The row states the actual comparison: the prices side by side and
    // who won, with the districts as the quiet second line.
    const prices = (item.compare_prices ?? []).filter((p) => p > 0);
    inner = (
      <>
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-medium tabular-nums text-gray-900">
            {prices.length >= 2
              ? prices.map((p) => formatPrice(p)).join(" vs ")
              : item.compare_preview || t("common.na")}
            {prices.length >= 2 && <span className="ml-1 text-xs font-normal text-gray-500">AZN</span>}
          </div>
          {prices.length >= 2 && item.compare_preview && (
            <div className="truncate text-xs text-gray-500">{item.compare_preview}</div>
          )}
        </div>
        {item.compare_winner != null && (
          <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-700 whitespace-nowrap">
            <Trophy className="h-3 w-3" aria-hidden />
            {t("compare.cards.listing_label").replace("{n}", String(item.compare_winner))}
          </span>
        )}
        {item.listing_count != null && item.compare_winner == null && (
          <span className="shrink-0 text-xs text-gray-500 whitespace-nowrap">
            {t("history.item.compare_count").replace("{n}", String(item.listing_count))}
          </span>
        )}
        {time}
      </>
    );
  } else {
    // Main line: place + attrs, truncating as one unit.
    const attrs: string[] = [];
    if (item.room_count != null) attrs.push(`${item.room_count} ${t("district.rooms.suffix")}`);
    if (item.area_m2 != null) attrs.push(`${item.area_m2} m²`);

    // Price cell: /calculate rows lead with the AI estimate (the
    // user's own asking price is what they typed in); analyze rows
    // lead with the listing's asking price.
    const isCalc = kind === "calculate";
    // The estimate at the grain every surface uses (lib/estimate).
    const shownPrice = isCalc
      ? (item.predicted_price != null ? roundEstimate(item.predicted_price, item.mode === "rent") : null)
      : item.price;
    const pct = item.overpriced_pct;

    inner = (
      <>
        <span
          className={`h-2 w-2 rounded-full shrink-0 ${dotColor(pct)}`}
          title={item.verdict ? translateVerdict(item.verdict, locale) : undefined}
        />
        <div className="min-w-0 flex-1 truncate text-sm">
          {item.mode === "rent" && (
            <span className="mr-1.5 inline-block align-[1px] text-xs font-semibold px-1.5 py-px rounded-full border bg-amber-50 text-amber-700 border-amber-200">
              {t("history.badge.rent")}
            </span>
          )}
          <span className="font-medium text-gray-900">
            {item.location_name || (attrs.length > 0 ? attrs.shift() : "N/A")}
          </span>
          {attrs.length > 0 && (
            <span className="text-gray-500"> · {attrs.join(" · ")}</span>
          )}
        </div>
        {shownPrice != null && (
          <span className="shrink-0 text-sm font-bold text-gray-900 tabular-nums whitespace-nowrap">
            {isCalc && <span className="mr-1 text-xs font-semibold text-gray-500">AI</span>}
            {formatPrice(shownPrice)}
            <span className="ml-0.5 text-xs font-semibold text-gray-500">
              {item.mode === "rent" ? "AZN/ay" : "AZN"}
            </span>
          </span>
        )}
        {pct != null && (
          <span
            className={`shrink-0 text-xs font-medium tabular-nums whitespace-nowrap ${
              pct < 0 ? "text-emerald-600" : pct > 5 ? "text-rose-600" : "text-gray-500"
            }`}
          >
            {signedPct(pct, 1)}
          </span>
        )}
        {time}
      </>
    );
  }

  if (item.href) {
    return (
      <Link
        href={item.href}
        className="no-scale flex items-center gap-3 px-3.5 py-2.5 hover:bg-gray-50 transition-colors"
      >
        {inner}
        <ChevronRight className="w-3.5 h-3.5 shrink-0 text-gray-300" />
      </Link>
    );
  }
  return <div className="flex items-center gap-3 px-3.5 py-2.5">{inner}</div>;
}

interface RecentActivityProps {
  kind: RecentKind;
}

export default function RecentActivity({ kind }: RecentActivityProps) {
  const t = useT();
  const { user, loading: authLoading } = useAuth();
  const [items, setItems] = useState<UnifiedItem[] | null>(null);

  useEffect(() => {
    if (authLoading) return;
    let cancelled = false;
    if (user) {
      getHistory(1, { source: sourceFilterFor(kind) })
        .then((data) => {
          if (cancelled) return;
          setItems(data.items.slice(0, FEED_SIZE).map((it) => fromHistoryItem(it, kind)));
        })
        .catch(() => {
          if (cancelled) return;
          setItems([]);
        });
    } else {
      const local = getRecentActivity().filter((it) => it.kind === kind);
      setItems(local.slice(0, FEED_SIZE).map(fromAnonItem));
    }
    return () => {
      cancelled = true;
    };
  }, [user, authLoading, kind]);

  if (authLoading || items === null) return null;
  if (items.length === 0) return null;

  const titleKey: TranslationKey =
    kind === "analyze"
      ? "recent.title.analyze"
      : kind === "calculate"
        ? "recent.title.calculate"
        : "recent.title.compare";

  return (
    <section className="mt-10 sm:mt-12">
      <div className="flex items-end justify-between mb-3">
        <h2 className="text-xs text-gray-500 font-semibold">
          {t(titleKey)}
        </h2>
        {user && (
          <Link
            href="/history"
            className="inline-flex items-center gap-1 text-xs font-semibold text-gray-900 underline-offset-2 underline decoration-gray-300 hover:decoration-gray-900"
          >
            {t("recent.viewall")}
            <ArrowRight className="w-3 h-3" />
          </Link>
        )}
      </div>
      <div className="rounded-2xl border border-gray-200 bg-white divide-y divide-gray-100 overflow-hidden">
        {items.map((it) => (
          <Row key={it.key} item={it} kind={kind} />
        ))}
      </div>
    </section>
  );
}
